import React from 'react';
import {
    EBallastOperationCodes,
    EBallastRecordCode,
    EBallastRecordState,
    TBallastBackendRecord,
} from '../../../../../redux/app/ballast/@types';
import BallastOperationPreview from '../previews/BallastOperationPreview';
import DeballastOperationPreview from '../previews/DeballastOperationPreview';
import ExchangeOperationPreview from '../previews/ExchangeOperationPreview';
import InternalOperationPreview from '../previews/InternalOperationPreview';
import AdditionalOperationPreview from '../previews/AdditionalOperationPreview';
import ExceptionalOperationPreview from '../previews/ExceptionalOperationPreview';
import AdditionalMissedEntryPreview from '../previews/AdditionalMissedEntryPreview';
import FailureBWMSOperationPreview from '../previews/FailureBWMSOperationPreview';
import BallastTableRows from '../_elements/BallastTableRows';
import BallastRecordParticipantsView from '../_elements/ParticipantsView';
import moment from 'moment';
import BallastAttachmentsPrintView from '../_elements/BallastAttachmentsPrintView';
import CleaningOperationPreview from '../previews/CleaningOperationPreview';

type OwnProps = {
    ballastRecord: TBallastBackendRecord;
    isPrint: boolean;
    theme?: "light" | "dark";
};
type BallastPreviewerProps = OwnProps;
function BallastPreviewer(props: BallastPreviewerProps) {
    const { ballastRecord, isPrint } = props;
    const isStrikedOff =
        ballastRecord.state === EBallastRecordState.STRIKED_OFF;

    const isSubmitterChiefOrMaster = ballastRecord.approvedBy
        ? ballastRecord.approvedBy.staffId ===
          ballastRecord.submittedBy?.staffId
        : false;

    const isEditOfApprovedRecord = !!ballastRecord.dateOfApprovedRecordEdit;

    function renderMain() {
        if (isEditOfApprovedRecord) return renderEditedApprovedRecord();
        return renderRecord(ballastRecord, isStrikedOff);
    }

    function renderEditedApprovedRecord() {
		if(!ballastRecord.dateOfApprovedRecordEdit) return null;
        return (
            <>
                {renderEditCorrectionLine()}
				{renderRecord({...ballastRecord, selectedDate: ballastRecord.dateOfApprovedRecordEdit}, isStrikedOff)}
                {renderStruckOffVersionOfEdit()}
            </>
        );
    }

    function renderEditCorrectionLine() {
        if (!ballastRecord.dateOfApprovedRecordEdit) return null;
        const formattedEditDate = moment(
            ballastRecord.selectedDate,
            'YYYY-MM-DD'
        ).format('DD-MMM-YYYY');

        return (
            <BallastTableRows
                record={ballastRecord}
                tableRows={[
                    [
                        formattedEditDate,
                        '-',
                        '-',
                        'ENTRY PERTAINING TO AN EARLIER INCORRECT OPERATIONAL ENTRY',
                    ],
                ]}
            />
        );
    }

    function renderStruckOffVersionOfEdit() {
        if (!ballastRecord.editVersions) return null;
        const oldVersionOfRecord = {
            ...ballastRecord.editVersions[0],
            state: EBallastRecordState.STRIKED_OFF,
        };
        return (
            <BallastPreviewer
                ballastRecord={oldVersionOfRecord}
                isPrint={false}
                theme={props.theme}
            />
        );
    }

    function renderRecord(
        recordToRender: TBallastBackendRecord,
        isStrikedOff: boolean = false
    ) {
        switch (recordToRender.recordCode) {
            case EBallastRecordCode.BALLAST:
                return (
                    <React.Fragment>
                        <BallastOperationPreview
                            ballastRecord={recordToRender}
                            isStrikedOff={isStrikedOff}
                        />
                        {isPrint ? (
                            <>
                                <BallastAttachmentsPrintView
                                    ballastRecord={ballastRecord}
                                />
                                <BallastRecordParticipantsView
                                    isSubmitterChiefOrMaster={
                                        isSubmitterChiefOrMaster
                                    }
                                    ballastRecord={ballastRecord}
                                    is2ndUserSection={
                                        ballastRecord.recordCode ===
                                            EBallastRecordCode.BALLAST &&
                                        ballastRecord.isBallastWaterTreatmentPlanUsed
                                    }
                                />
                            </>
                        ) : null}
                    </React.Fragment>
                );
            case EBallastRecordCode.DEBALLAST:
                return (
                    <React.Fragment>
                        <DeballastOperationPreview
                            ballastRecord={recordToRender}
                            isStrikedOff={isStrikedOff}
                        />
                        {isPrint ? (
                            <>
                                <BallastAttachmentsPrintView
                                    ballastRecord={ballastRecord}
                                />
                                <BallastRecordParticipantsView
                                    isSubmitterChiefOrMaster={
                                        isSubmitterChiefOrMaster
                                    }
                                    ballastRecord={ballastRecord}
                                    is2ndUserSection={
                                        ballastRecord.recordCode ===
                                            EBallastRecordCode.BALLAST &&
                                        ballastRecord.isBallastWaterTreatmentPlanUsed
                                    }
                                />
                            </>
                        ) : null}
                    </React.Fragment>
                );
            case EBallastRecordCode.EXCHANGE:
                return (
                    <React.Fragment>
                        <ExchangeOperationPreview
                            ballastRecord={recordToRender}
                            isStrikedOff={isStrikedOff}
                        />
                        {isPrint ? (
                            <>
                                <BallastAttachmentsPrintView
                                    ballastRecord={ballastRecord}
                                />
                                <BallastRecordParticipantsView
                                    isSubmitterChiefOrMaster={
                                        isSubmitterChiefOrMaster
                                    }
                                    ballastRecord={ballastRecord}
                                    is2ndUserSection={
                                        ballastRecord.recordCode ===
                                            EBallastRecordCode.BALLAST &&
                                        ballastRecord.isBallastWaterTreatmentPlanUsed
                                    }
                                />
                            </>
                        ) : null}
                    </React.Fragment>
                );
            case EBallastRecordCode.INTERNAL:
                return (
                    <React.Fragment>
                        <InternalOperationPreview
                            ballastRecord={recordToRender}
                            isStrikedOff={isStrikedOff}
                        />
                        {isPrint ? (
                            <>
                                <BallastAttachmentsPrintView
                                    ballastRecord={ballastRecord}
                                />
                                <BallastRecordParticipantsView
                                    isSubmitterChiefOrMaster={
                                        isSubmitterChiefOrMaster
                                    }
                                    ballastRecord={ballastRecord}
                                    is2ndUserSection={
                                        ballastRecord.recordCode ===
                                            EBallastRecordCode.BALLAST &&
                                        ballastRecord.isBallastWaterTreatmentPlanUsed
                                    }
                                />
                            </>
                        ) : null}
                    </React.Fragment>
                );
            case EBallastRecordCode.ADDITIONAL: {
                if (
                    ballastRecord.operationCode ===
                        EBallastOperationCodes.ADDITIONAL_PROCEDURES ||
                    ballastRecord.operationCode ===
                        EBallastOperationCodes.ADDITIONAL_PROCEDURES_MEPC80
                )
                    return (
                        <React.Fragment>
                            <AdditionalOperationPreview
                                ballastRecord={recordToRender}
                                isStrikedOff={isStrikedOff}
                            />
                            {isPrint ? (
                                <>
                                    <BallastAttachmentsPrintView
                                        ballastRecord={ballastRecord}
                                    />
                                    <BallastRecordParticipantsView
                                        isSubmitterChiefOrMaster={
                                            isSubmitterChiefOrMaster
                                        }
                                        ballastRecord={ballastRecord}
                                    />
                                </>
                            ) : null}
                        </React.Fragment>
                    );
                if (
                    ballastRecord.operationCode ===
                        EBallastOperationCodes.ADDITIONAL_PROCEDURES_MISSED_ENTRY ||
                    ballastRecord.operationCode ===
                        EBallastOperationCodes.ADDITIONAL_PROCEDURES_MISSED_ENTRY_MEPC80
                )
                    return (
                        <React.Fragment>
                            <AdditionalMissedEntryPreview
                                ballastRecord={recordToRender}
                                isStrikedOff={isStrikedOff}
                            />
                            {isPrint ? (
                                <>
                                    <BallastAttachmentsPrintView
                                        ballastRecord={ballastRecord}
                                    />
                                    <BallastRecordParticipantsView
                                        isSubmitterChiefOrMaster={
                                            isSubmitterChiefOrMaster
                                        }
                                        ballastRecord={ballastRecord}
                                    />
                                </>
                            ) : null}
                        </React.Fragment>
                    );
            }
            case EBallastRecordCode.EXCEPTIONAL:
                return (
                    <React.Fragment>
                        <ExceptionalOperationPreview
                            ballastRecord={recordToRender}
                            isStrikedOff={isStrikedOff}
                        />
                        {isPrint ? (
                            <>
                                <BallastAttachmentsPrintView
                                    ballastRecord={ballastRecord}
                                />
                                <BallastRecordParticipantsView
                                    isSubmitterChiefOrMaster={
                                        isSubmitterChiefOrMaster
                                    }
                                    ballastRecord={ballastRecord}
                                    is2ndUserSection={
                                        ballastRecord.recordCode ===
                                            EBallastRecordCode.BALLAST &&
                                        ballastRecord.isBallastWaterTreatmentPlanUsed
                                    }
                                />
                            </>
                        ) : null}
                    </React.Fragment>
                );
            case EBallastRecordCode.FAILURE:
                return (
                    <React.Fragment>
                        <FailureBWMSOperationPreview
                            ballastRecord={recordToRender}
                            isStrikedOff={isStrikedOff}
                        />
                        {isPrint ? (
                            <>
                                <BallastAttachmentsPrintView
                                    ballastRecord={ballastRecord}
                                />
                                <BallastRecordParticipantsView
                                    isSubmitterChiefOrMaster={
                                        isSubmitterChiefOrMaster
                                    }
                                    ballastRecord={ballastRecord}
                                    is2ndUserSection={
                                        ballastRecord.recordCode ===
                                            EBallastRecordCode.BALLAST &&
                                        ballastRecord.isBallastWaterTreatmentPlanUsed
                                    }
                                />
                            </>
                        ) : null}
                    </React.Fragment>
                );
            case EBallastRecordCode.CLEANING:
                return (
                    <React.Fragment>
                        <CleaningOperationPreview
                            ballastRecord={recordToRender}
                            isStrikedOff={isStrikedOff}
                        />
                        {isPrint ? (
                            <>
                                <BallastAttachmentsPrintView
                                    ballastRecord={ballastRecord}
                                />
                                <BallastRecordParticipantsView
                                    isSubmitterChiefOrMaster={
                                        isSubmitterChiefOrMaster
                                    }
                                    ballastRecord={ballastRecord}
                                    is2ndUserSection={
                                        ballastRecord.recordCode ===
                                            EBallastRecordCode.BALLAST &&
                                        ballastRecord.isBallastWaterTreatmentPlanUsed
                                    }
                                />
                            </>
                        ) : null}
                    </React.Fragment>
                );
            default:
                return null;
        }
    }
    return renderMain();
}

export default BallastPreviewer;
